import { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import apiService from '../services/apiService';
import logger from '../utils/logger';
import * as appStorage from '../utils/appIndexedDB';
// 本地持久化：`isA2uiV08BotReplyPayload` 經 `attachPersistA2uiV08` 寫入 `isA2uiV08BotReply`
import { attachPersistA2uiV08 } from '../utils/attachPersistA2uiV08';

const TOKEN_STORAGE_KEY = 'mygenie_token';
const LAST_SELECTED_CHAT_ID_KEY = 'mygenie_last_selected_chat_id';

/**
 * 這個分頁目前停在空白新對話（AGUI-1205）。
 *
 * LAST_SELECTED_CHAT_ID_KEY 刻意不記空白新對話，F5 時就會還原成上一則對話（側欄跟著反白）。
 * 存 sessionStorage 而不是 IndexedDB：跟 App.js 的 `agui.activeView` 一樣只管「這個分頁按 F5 回到哪」，
 * 開新分頁仍照舊還原上次的對話。停在新對話不需要後端：createNewChat 只建前端暫存對話，
 * 後端聊天室要到送出第一則訊息（或上傳附件）時才建立。
 */
const NEW_CHAT_SESSION_KEY = 'agui.onNewChat';

const readOnNewChat = () => {
  try {
    return window.sessionStorage.getItem(NEW_CHAT_SESSION_KEY) === '1';
  } catch {
    return false;
  }
};

const writeOnNewChat = (on) => {
  try {
    if (on) window.sessionStorage.setItem(NEW_CHAT_SESSION_KEY, '1');
    else window.sessionStorage.removeItem(NEW_CHAT_SESSION_KEY);
  } catch {
    // 無痕或封鎖儲存時就不記，F5 照舊還原上一則對話
  }
};

// 進階聊天歷史管理 Hook - 支援多裝置同步
// useWebSocket: 使用 WebSocket 收發訊息時為 true，儲存到 IndexedDB 的 chatHistory 的 isHuman 會為 true；取消使用 WebSocket 時為 false，isHuman 會為 false
export const useChatHistoryWithAdvancedSync = (auth = null, wsConnected = false, useWebSocket = false, isHumanMode = false) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);

  // 等待真人客服期間，若 WebSocket 超過此時間未收到任何訊息，發送忙線提示（15 分鐘）
  const WS_IDLE_TIMEOUT = 15 * 60 * 1000;
  // WebSocket 連線後最後一次收到訊息的時間（用於 15 分鐘無訊息時發送忙線提示）
  const lastWsMessageReceivedAtRef = useRef(null);
  // 跟踪每個聊天是否已發送過「忙線中」提示
  const busyLineMessageSentRef = useRef(new Set());
  // 記憶體中的最新 chatHistory
  const chatHistoryRef = useRef([]);
  // 同步鎖：擋掉 initializeChatHistory() 因 auth.user 短時間內多次變化而被重複觸發
  const initializingRef = useRef(false);

  // 統一判斷目前是否視為 WebSocket 模式
  const isWebSocketMode = () => {
    if (wsConnected) return true;
    try {
      const flag = appStorage.getItemSync('nowIsWebSocket');
      logger.info("WebSocket", "isWebSocketMode", { flag });
      return flag === 'true';
    } catch (e) {
      return wsConnected;
    }
  };

  const hasConversationRecord = useCallback((chat) => {
    return Array.isArray(chat?.messages) && chat.messages.length > 0;
  }, []);

  const isRestorableChat = useCallback((chat) => {
    return Boolean(chat) && !chat.isDeleted && !chat.isTemporary && hasConversationRecord(chat);
  }, [hasConversationRecord]);

  const pickChatIdForRestore = useCallback((history = [], lastSelectedChatId = null) => {
    if (!Array.isArray(history) || history.length === 0) return null;
    if (lastSelectedChatId) {
      const selected = history.find(chat => chat.id === lastSelectedChatId);
      if (isRestorableChat(selected)) return selected.id;
    }

    const candidates = history.filter(chat => isRestorableChat(chat));
    if (candidates.length === 0) return null;
    candidates.sort((a, b) =>
      new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime()
    );
    return candidates[0].id;
  }, [isRestorableChat]);

  // 產生本地訊息 UUID（瀏覽器支援 crypto.randomUUID 時優先使用）
  const generateMessageId = () => {
    try {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
    } catch (e) {
      // ignore
    }
    return `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  // 確保訊息結構完整；寫入本地儲存時不覆寫既有 isHuman，僅在 isHuman 未設定時才用 defaultIsHuman（guest）或 false（非 guest）
  const normalizeMessage = (msg, defaultIsHuman = undefined) => {
    if (!msg) return null;
    const createdAt =
      msg.createdAt ||
      (msg.timestamp ? new Date(msg.timestamp).toISOString() : null) ||
      (msg.time ? new Date(msg.time).toISOString() : null) ||
      new Date().toISOString();
    // 已有 isHuman 則保留（之前的對話不因目前是否用 WebSocket 而改變）；未設定時 guest 用 defaultIsHuman、非 guest 用 false
    const isHuman = msg.isHuman !== undefined
      ? msg.isHuman
      : (defaultIsHuman !== undefined && msg.sender === 'guest')
        ? defaultIsHuman
        : false;

    const normalized = {
      ...msg,
      id: msg.id || generateMessageId(),
      createdAt,
      room_id: msg.room_id || null,
      isHuman,
      replyToId: msg.replyToId ?? null,
    };
    return attachPersistA2uiV08(normalized, msg);
  };

  const normalizeMessages = (messages = [], defaultIsHuman = undefined) => {
    const normalized = messages.map(m => normalizeMessage(m, defaultIsHuman)).filter(Boolean);
    // 為機器人/系統回覆設定 replyToId：若上一則為用戶訊息，則指向該訊息的 id
    return normalized.map((msg, i) => {
      if (i > 0 && msg.sender !== 'guest' && normalized[i - 1].sender === 'guest') {
        return { ...msg, replyToId: normalized[i - 1].id };
      }
      return msg;
    });
  };

  const normalizeHistory = (history = [], defaultIsHuman = undefined) =>
    history.map(chat => ({
      ...chat,
      messages: normalizeMessages(chat.messages, defaultIsHuman),
    }));

  // 儲存到本地；依目前是否使用 WebSocket 寫入 isHuman（使用 WebSocket 時 true，否則 false）
  const saveToStorage = useCallback(async (history) => {
    const normalizedHistory = normalizeHistory(history, useWebSocket);
    chatHistoryRef.current = normalizedHistory;
    await appStorage.setItem('chatHistory', JSON.stringify(normalizedHistory));
    return normalizedHistory;
  }, [useWebSocket]);

  // 獲取當前聊天（若該筆為 isDeleted 則不視為當前對話，不顯示）
  const getCurrentChat = useCallback(() => {
    const chat = chatHistory.find(chat => chat.id === currentChatId);
    if (chat && chat.isDeleted === true) return undefined;
    return chat;
  }, [chatHistory, currentChatId]);

  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  // 監聽網路狀態
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 從後端載入聊天歷史（作為主來源）
  // TODO: 恢復 apiService.getChatHistory 後再啟用後端合併流程
  const loadFromBackend = useCallback(async () => {
    const token = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      logger.warn('聊天', '無法從後端載入：未登入');
      return false;
    }

    logger.info('聊天', '從後端載入暫停（getChatHistory TODO），將降級為本地載入');
    return false;
  }, []);



  // 從本地載入（僅在後端不可用時使用）
  const loadFromLocal = useCallback(async () => {
    const savedHistory = await appStorage.getItem('chatHistory');
    if (savedHistory) {
      const history = normalizeHistory(JSON.parse(savedHistory));
      chatHistoryRef.current = history;
      setChatHistory(history);

      // 本次開站若剛執行過明確登入動作（login/ssoLogin/mplusLogin），預設顯示新對話，
      // 不還原上次選中的對話；歷史清單仍完整載入，使用者可自行點選舊對話。
      // LAST_SELECTED_CHAT_ID_KEY 本身不清除，讓之後純被動 resume（無登入動作)仍能正確還原。
      const justLoggedIn = auth?.consumeJustLoggedIn?.() ?? false;
      // F5 前停在空白新對話：留在新對話，沿用既有那筆空白暫存對話，避免每按一次 F5 就多留一筆（AGUI-1205）
      const onNewChat = !justLoggedIn && readOnNewChat();
      const lastSelectedChatId = await appStorage.getItem(LAST_SELECTED_CHAT_ID_KEY);
      let chatIdToRestore;
      if (justLoggedIn) {
        chatIdToRestore = null;
      } else if (onNewChat) {
        const blank = history.filter(chat => chat.isTemporary && !chat.isDeleted && !hasConversationRecord(chat)).pop();
        chatIdToRestore = blank ? blank.id : null;
      } else {
        chatIdToRestore = pickChatIdForRestore(history, lastSelectedChatId);
      }
      setCurrentChatId(chatIdToRestore);

      logger.info('聊天', '從本地載入聊天歷史', {
        chatCount: history.length,
        restoredChatId: chatIdToRestore,
        justLoggedIn,
        onNewChat,
      });
      return true;
    }
    return false;
  }, [pickChatIdForRestore, hasConversationRecord]);

  // 初始化載入（只執行一次，且只在登入成功後）
  useEffect(() => {
    // 等待認證檢查完成
    if (!auth || auth.isLoading) {
      logger.info('聊天', '等待認證檢查完成');
      return;
    }

    // 只有在登入成功後才初始化（檢查 user 是否存在）
    if (!auth.user) {
      logger.info('聊天', '未登入，跳過聊天歷史初始化');
      return;
    }

    // 如果已經初始化過，不再重複執行
    if (isInitialized) {
      return;
    }

    // isInitialized 是 useState，只有在 initializeChatHistory() 這個 async 函式真正
    // 執行完（setIsInitialized(true) 觸發重新渲染)後才會反映最新值；在那之前，若
    // auth.user 又短時間內變化多次（例如 SSO/M+ 流程內部「先 logout 清空 user、
    // 再重新登入設回新 user」),這個 effect 會在同一輪 initializeChatHistory() 還
    // 沒跑完時就被重新觸發，導致 loadFromLocal() 被呼叫兩次：第一次正確讀到
    // consumeJustLoggedIn()===true 顯示新對話，第二次（旗標已被讀掉歸零)又把畫面
    // 覆寫回還原上次對話，兩次的結果互相覆蓋，最終顯示哪個純粹看誰的 async 先完成。
    // isInitialized 這個 state 沒辦法同步擋掉這種競爭，改用 ref 在本次 effect 同步
    // 執行的當下就鎖住，確保同一輪初始化只會真正跑一次。
    if (initializingRef.current) {
      return;
    }
    initializingRef.current = true;

    const initializeChatHistory = async () => {
      const token = await appStorage.getItem(TOKEN_STORAGE_KEY);

      try {
        if (token && isOnline) {
          logger.info('聊天', '初始化：優先從後端載入');
          const success = await loadFromBackend();

          if (success) {
            setIsInitialized(true);
            return;
          }
        }

        logger.info('聊天', '降級：使用本地載入');
        const localSuccess = await loadFromLocal();

        if (!localSuccess) {
          // 沒有本地聊天記錄時，不自動創建聊天室，等待用戶主動創建
          setChatHistory([]);
          setCurrentChatId(null);
          logger.info('聊天', '沒有聊天記錄，等待用戶主動創建新聊天');
        }

        setIsInitialized(true);

      } catch (error) {
        logger.error('聊天', '初始化失敗', { error: error.message });
        await loadFromLocal();
        setIsInitialized(true);
      }
    };

    initializeChatHistory();
  }, [auth?.user, auth?.isLoading, loadFromBackend, loadFromLocal, isInitialized, isOnline]);

  // 首次初始化完成「之後」的明確登入動作（例如同一分頁內登出後重新登入，未整頁重新整理）
  // 也要重置為新對話：上面的初始化流程只在 isInitialized 為 false 時執行一次，
  // 不會因為之後又登出重登而重新觸發，必須另外處理，否則旗標永遠不會被消費。
  useEffect(() => {
    if (!isInitialized) return;
    if (!auth?.user) return;
    if (auth?.consumeJustLoggedIn?.()) {
      setCurrentChatId(null);
      logger.info('聊天', '偵測到登入動作（非開站首次初始化），重置為新對話');
    }
  }, [auth, auth?.user, isInitialized]);

  // 記錄最後一次「可還原」的聊天室（排除空白新對話頁）
  useEffect(() => {
    if (!currentChatId) return;
    const currentChat = chatHistory.find(chat => chat.id === currentChatId);
    if (!isRestorableChat(currentChat)) return;
    void appStorage.setItem(LAST_SELECTED_CHAT_ID_KEY, currentChatId);
  }, [currentChatId, chatHistory, isRestorableChat]);

  // 記錄這個分頁是否停在空白新對話（AGUI-1205）：可還原的對話＝不是；其餘（空白暫存對話、沒有選任何對話）＝是。
  // ⛔ 等初始化還原完才記，否則開站時 currentChatId 還是初始的 null，會被誤記成停在新對話
  useEffect(() => {
    if (!isInitialized) return;
    const currentChat = currentChatId ? chatHistory.find(chat => chat.id === currentChatId) : null;
    writeOnNewChat(!isRestorableChat(currentChat));
  }, [isInitialized, currentChatId, chatHistory, isRestorableChat]);

  // 創建新對話（僅重置前端狀態，不呼叫後端建立聊天室）
  const createNewChat = async () => {
    const currentChat = getCurrentChat();
    let updatedHistory = [...chatHistory];

    if (currentChat && currentChat.messages.some(msg => msg.sender === 'guest')) {
      const firstUserMessage = currentChat.messages.find(msg => msg.sender === 'guest');
      if (firstUserMessage) {
        currentChat.title = firstUserMessage.text.slice(0, 30) +
          (firstUserMessage.text.length > 30 ? '...' : '');
      }
      delete currentChat.isTemporary;

      updatedHistory = updatedHistory.map(chat =>
        chat.id === currentChat.id ? currentChat : chat
      );
    }

    const activeTemporary = currentChat && currentChat.isTemporary;
    const hasUserMessage = currentChat && currentChat.messages.some(msg => msg.sender === 'guest');

    // 若當前已是空白臨時對話，直接重用，避免產生多筆空白新對話
    if (activeTemporary && !hasUserMessage) {
      const resetCurrent = {
        ...currentChat,
        title: '新對話',
        messages: [],
        updatedAt: new Date().toISOString(),
      };
      const resetHistory = updatedHistory.map(chat =>
        chat.id === currentChat.id ? resetCurrent : chat
      );
      setChatHistory(resetHistory);
      setCurrentChatId(resetCurrent.id);
      await saveToStorage(resetHistory);
      logger.info('聊天', '重用現有空白新對話', { chatId: resetCurrent.id });
      return resetCurrent;
    }

    const newChat = {
      id: `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      title: '新對話',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isTemporary: true,
    };

    updatedHistory = [...updatedHistory, newChat];
    setChatHistory(updatedHistory);
    setCurrentChatId(newChat.id);
    await saveToStorage(updatedHistory);
    logger.info('聊天', '建立本地臨時新對話（尚未初始化後端）', { chatId: newChat.id });
    return newChat;
  };

  // 首則訊息送出成功取得後端 chat_id 後，將臨時對話升級為正式對話
  const promoteTemporaryChat = useCallback((temporaryChatId, serverChatId, fallbackChat = null) => {
    if (!temporaryChatId || !serverChatId) return null;
    let promoted = null;

    setChatHistory(prevHistory => {
      let found = false;
      const next = prevHistory.map(chat => {
        if (chat.id !== temporaryChatId) return chat;
        found = true;
        promoted = {
          ...chat,
          id: serverChatId,
          isTemporary: false,
          updatedAt: new Date().toISOString(),
        };
        return promoted;
      });

      // setState 尚未同步時，可能還找不到臨時對話，需補上一筆正式對話避免側欄空白
      if (!found) {
        promoted = {
          id: serverChatId,
          title: fallbackChat?.title || '新對話',
          messages: Array.isArray(fallbackChat?.messages) ? fallbackChat.messages : [],
          createdAt: fallbackChat?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isTemporary: false,
        };
        next.push(promoted);
      }

      saveToStorage(next);
      return next;
    });

    setCurrentChatId(prevId => (prevId === temporaryChatId ? serverChatId : prevId));
    logger.info('聊天', '臨時對話已升級為正式對話', { temporaryChatId, serverChatId });
    return promoted;
  }, [saveToStorage]);

  // 發送「轉專人客服忙線中」提示（WebSocket 連線後 15 分鐘未收到任何訊息時）
  const sendBusyLineMessage = useCallback(async (chatId) => {
    if (busyLineMessageSentRef.current.has(chatId)) return;

    let updatedHistory = null;

    flushSync(() => {
      setChatHistory(prevHistory => {
        const chat = prevHistory.find(c => c.id === chatId);
        if (!chat) return prevHistory;

        const lastMessage = chat.messages[chat.messages.length - 1];
        if (lastMessage && lastMessage.isBusyLineMessage) {
          busyLineMessageSentRef.current.add(chatId);
          return prevHistory;
        }

        const hasUserMessage = chat.messages.some(msg => msg.sender === 'guest');
        if (!hasUserMessage) return prevHistory;

        const busyLineText = '轉專人客服目前忙線中，謝謝您的耐心等候；若要取消轉專人服務，請點選「取消轉專人」鈕。';
        const busyLineMsg = normalizeMessage({
          sender: 'system',
          text: busyLineText,
          isBusyLineMessage: true
        });

        const updatedMessages = normalizeMessages([...chat.messages, busyLineMsg]);
        busyLineMessageSentRef.current.add(chatId);

        const result = prevHistory.map(c =>
          c.id === chatId ? {
            ...c,
            messages: updatedMessages,
            updatedAt: new Date().toISOString(),
          } : c
        );
        updatedHistory = result;
        return result;
      });
    });

    if (updatedHistory) {
      void saveToStorage(updatedHistory);
      logger.info('聊天', '自動發送忙線提示（WebSocket 15 分鐘無訊息）', { chatId });
    }
  }, [saveToStorage]);

  // WebSocket 收到訊息時呼叫，用來重置 15 分鐘無訊息計時
  const notifyWsMessageReceived = useCallback(() => {
    lastWsMessageReceivedAtRef.current = Date.now();
  }, []);

  // 新增聊天
  const addNewChat = async (chat_id) => {
    const currentChat = getCurrentChat();
    let updatedHistory = [...chatHistory];

    let newChat;
    newChat = {
      id: chat_id, // 使用 API 回傳的 chat_id
      title: '新對話',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isTemporary: false, // 已經在後端創建，不是臨時聊天
    };

    updatedHistory = [...updatedHistory, newChat];

    setChatHistory(updatedHistory);
    setCurrentChatId(newChat.id); // 切換到新聊天
    saveToStorage(updatedHistory);
    logger.info('聊天', '新增聊天!', {  chatId: newChat.id, isTemporary: newChat.isTemporary  });

    return updatedHistory;
  };

  // 更新對話訊息（訊息內容已由後端於 /send 當下同步落地，這裡僅維護本地狀態）
  const updateChatMessages = async (chatId, messages, options = {}) => {
    const { append = false } = options;
    const normalizedMessages = normalizeMessages(messages);

    const savedHistoryRaw = await appStorage.getItem('chatHistory');
    const pendingKey = `pendingToolList_${chatId}`;
    let messagesToSave = normalizedMessages;
    const pendingRaw = await appStorage.getItem(pendingKey);
    if (pendingRaw) {
      try {
        const pending = JSON.parse(pendingRaw);
        const toolList = pending?.tool_list;
        if (Array.isArray(toolList) && toolList.length > 0 && messagesToSave.length > 0) {
          const toolCalls = toolList.flatMap(t => t.tool_calls || []);
          if (toolCalls.length > 0) {
            const lastMsg = messagesToSave[messagesToSave.length - 1];
            if (lastMsg && lastMsg.sender === 'chatbot') {
              messagesToSave = [
                ...messagesToSave.slice(0, -1),
                { ...lastMsg, tool_calls: toolCalls }
              ];
            }
          }
        }
        await appStorage.removeItem(pendingKey);
      } catch (e) {
        logger.warn('聊天', '合併 pending tool_list 失敗', { error: e.message });
        try { await appStorage.removeItem(pendingKey); } catch (_) {}
      }
    }

    let nextHistory = null;

    flushSync(() => {
      setChatHistory(prevHistory => {
        let currentHistory = prevHistory;
        const existingChat = currentHistory.find(chat => chat.id === chatId);

        if (!existingChat && savedHistoryRaw) {
          try {
            const parsedHistory = JSON.parse(savedHistoryRaw);
            const foundChat = parsedHistory.find(chat => chat.id === chatId);
            if (foundChat) {
              currentHistory = parsedHistory;
              logger.warn('聊天', '從本地儲存中找到聊天', { chatId });
            }
          } catch (e) {
            logger.error('聊天', '解析本地儲存失敗', { error: e.message });
          }
        }

        const mergedMessages = (() => {
          if (!append) return messagesToSave;
          const baseChat = currentHistory.find(c => c.id === chatId);
          const existingMessages = Array.isArray(baseChat?.messages) ? baseChat.messages : [];
          if (!existingMessages.length) return messagesToSave;
          const existingIds = new Set(
            existingMessages.filter(m => typeof m?.id === 'string' && m.id.length > 0).map(m => m.id)
          );
          const toAppend = messagesToSave.filter(m => !m?.id || !existingIds.has(m.id));
          return [...existingMessages, ...toAppend];
        })();

        const result = currentHistory.map(chat =>
          chat.id === chatId ? {
            ...chat,
            messages: mergedMessages,
            updatedAt: new Date().toISOString(),
            title: (() => {
              if (chat.title !== '新對話') return chat.title;
              const firstUserText = mergedMessages.find(msg => msg.sender === 'guest')?.text;
              if (!firstUserText) return chat.title;
              const trimmed = firstUserText.slice(0, 30);
              return trimmed + (firstUserText.length > 30 ? '...' : '');
            })()
          } : chat
        );

        if (!result.find(chat => chat.id === chatId)) {
          logger.warn('聊天', '找不到聊天，創建新記錄', { chatId });
          const newChat = {
            id: chatId,
            title: messagesToSave.find(msg => msg.sender === 'guest')?.text?.slice(0, 30) || '新聊天',
            messages: messagesToSave,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            isTemporary: false,
          };
          result.push(newChat);
        }

        nextHistory = result;

        return result;
      });
    });

    if (nextHistory) {
      await saveToStorage(nextHistory);
    }

    logger.info('聊天', '更新聊天訊息', { chatId, messageCount: normalizedMessages.length });
  };

  // 更新對話標題（僅本地狀態；標題重新命名目前無對應的後端 API）
  const updateChatTitle = async (chatId, title) => {
    const updatedHistory = chatHistory.map(chat =>
      chat.id === chatId ? {
        ...chat,
        title,
        updatedAt: new Date().toISOString(),
      } : chat
    );
    setChatHistory(updatedHistory);
    await saveToStorage(updatedHistory);
    logger.info('聊天', '更新對話標題', { chatId, title });
  };

  /**
   * 更新單則訊息之使用者回饋（ag_tb_message_feedback；先樂觀更新本地，再呼叫 POST /web/messages/feedback）
   * @param {boolean|null} userFeedback — true 按讚、false 倒讚、null 清除
   * @param {string|null|undefined} userComment — 使用者補充說明
   */
  const updateMessageUserFeedback = useCallback(async (chatId, messageId, userFeedback, userComment) => {
    const targetChat = chatHistory.find((c) => c.id === chatId);
    if (!targetChat) {
      logger.warn('聊天', 'updateMessageUserFeedback：找不到對話', { chatId });
      return;
    }
    const prevMsg = targetChat.messages?.find((m) => m.id === messageId);
    const previousFeedback = prevMsg?.userFeedback;
    const previousComment = prevMsg?.userComment;

    if (targetChat.isTemporary) {
      const updatedHistory = chatHistory.map((chat) => {
        if (chat.id !== chatId) return chat;
        return {
          ...chat,
          messages: chat.messages.map((m) =>
            m.id === messageId
              ? { ...m, userFeedback, ...(userComment !== undefined ? { userComment } : {}) }
              : m
          ),
          updatedAt: new Date().toISOString(),
        };
      });
      setChatHistory(updatedHistory);
      saveToStorage(updatedHistory);
      return;
    }

    const token = await appStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      logger.warn('聊天', 'updateMessageUserFeedback：未登入，略過 API');
      throw new Error('尚未登入');
    }

    const optimistic = chatHistory.map((chat) => {
      if (chat.id !== chatId) return chat;
      return {
        ...chat,
        messages: chat.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                userFeedback,
                ...(userComment !== undefined ? { userComment } : {}),
              }
            : m
        ),
        updatedAt: new Date().toISOString(),
      };
    });
    setChatHistory(optimistic);
    saveToStorage(optimistic);

    try {
      const res = await apiService.submitMessageFeedback({
        chatId,
        messageId,
        userFeedback,
        userComment,
        commentOnly: userComment !== undefined,
      });
      if (!res || res.code !== 200) {
        throw new Error(res?.message || `code ${res?.code ?? 'unknown'}`);
      }
      logger.success('聊天', '訊息回饋已同步', { chatId, messageId, userFeedback });
    } catch (err) {
      const reverted = chatHistory.map((chat) => {
        if (chat.id !== chatId) return chat;
        return {
          ...chat,
          messages: chat.messages.map((m) =>
            m.id === messageId
              ? { ...m, userFeedback: previousFeedback, userComment: previousComment }
              : m
          ),
          updatedAt: new Date().toISOString(),
        };
      });
      setChatHistory(reverted);
      saveToStorage(reverted);
      logger.error('聊天', '訊息回饋同步失敗', { error: err?.message ?? String(err) });
      throw err;
    }
  }, [chatHistory, saveToStorage]);

  // 刪除聊天：本地標記 isDeleted=true，並呼叫後端 API 軟刪除 ag_tb_conversation（change: remove-legacy-sync）
  const deleteChat = async (chatId) => {
    const chatToDelete = chatHistory.find(chat => chat.id === chatId);
    const updatedHistory = chatHistory.map(chat =>
      chat.id === chatId
        ? { ...chat, isDeleted: true }
        : chat
    );
    setChatHistory(updatedHistory);
    await saveToStorage(updatedHistory);

    if (chatToDelete && !chatToDelete.isTemporary) {
      try {
        await apiService.deleteChatExternal(chatId);
        logger.success('聊天', '刪除聊天已同步至後端', { chatId });
      } catch (err) {
        logger.error('聊天', '刪除聊天同步至後端失敗', { chatId, error: err?.message ?? String(err) });
      }
    }

    if (currentChatId === chatId) {
      const nextChatId = pickChatIdForRestore(updatedHistory, null);
      setCurrentChatId(nextChatId);
    }

    logger.info('聊天', '刪除聊天（已標記 isDeleted）', { chatId });
  };

  // 切換到指定聊天
  const switchToChat = useCallback(async (chatId) => {
    const currentChat = getCurrentChat();
    if (
      currentChat &&
      currentChat.isTemporary &&
      currentChat.messages.some(msg => msg.sender === 'guest')
    ) {
      const firstUserMessage = currentChat.messages.find(msg => msg.sender === 'guest');
      const computedTitle = firstUserMessage
        ? firstUserMessage.text.slice(0, 30) + (firstUserMessage.text.length > 30 ? '...' : '')
        : currentChat.title || '新對話';

      const finalizedCurrent = {
        ...currentChat,
        title: computedTitle,
        updatedAt: new Date().toISOString(),
        isTemporary: false,
      };

      const updatedHistory = chatHistory.map(chat =>
        chat.id === currentChat.id ? finalizedCurrent : chat
      );

      flushSync(() => {
        setChatHistory(updatedHistory);
      });

      await saveToStorage(updatedHistory);
    }

    setCurrentChatId(chatId);
  }, [chatHistory, getCurrentChat, saveToStorage]);

  // 獲取當前聊天的訊息
  const getCurrentMessages = () => {
    const currentChat = getCurrentChat();
    return currentChat ? currentChat.messages : [];
  };

  // 若當前對話為已刪除（isDeleted），自動切換到最後一筆未刪除對話或 null，避免顯示已刪除對話
  useEffect(() => {
    if (!currentChatId) return;
    const currentChat = chatHistory.find(chat => chat.id === currentChatId);
    if (currentChat && currentChat.isDeleted === true) {
      const nonDeleted = chatHistory.filter(c => !c.isDeleted);
      if (nonDeleted.length > 0) {
        setCurrentChatId(nonDeleted[nonDeleted.length - 1].id);
      } else {
        setCurrentChatId(null);
      }
    }
  }, [chatHistory, currentChatId]);

  // 监听 WebSocket 连接状态变化
  useEffect(() => {
    if (wsConnected) {
      // WebSocket 剛連上：開始 15 分鐘無訊息計時
      lastWsMessageReceivedAtRef.current = Date.now();
    } else {
      lastWsMessageReceivedAtRef.current = null;
      if (currentChatId) {
        busyLineMessageSentRef.current.delete(currentChatId);
      }
    }
  }, [wsConnected, currentChatId]);

  // 等待真人客服期間，WebSocket 連線後 15 分鐘未收到任何訊息時，發送忙線提示
  useEffect(() => {
    // 這句話的內容專屬於轉專人流程（提到「取消轉專人」鈕），只有真的在等真人客服時才該出現。
    // 少了這道判斷，AI 對話（wsMode === 'ai'）只要 WS 連著閒置 15 分鐘也會被塞進這句話——
    // 使用者在純 AI 對話裡莫名收到轉專人提示，prod 已有實際案例。
    if (!isHumanMode) return;
    if (!wsConnected || !currentChatId) return;

    const checkWsIdle = () => {
      const lastAt = lastWsMessageReceivedAtRef.current;
      if (lastAt == null) return;
      if (busyLineMessageSentRef.current.has(currentChatId)) return;

      const elapsed = Date.now() - lastAt;
      if (elapsed >= WS_IDLE_TIMEOUT) {
        sendBusyLineMessage(currentChatId);
      }
    };

    checkWsIdle();
    const interval = setInterval(checkWsIdle, 30000);
    return () => clearInterval(interval);
  }, [isHumanMode, wsConnected, currentChatId, sendBusyLineMessage]);

  return {
    chatHistory,
    currentChatId,
    isLoading,
    error,
    isOnline,
    // 登入後的本地/後端歷史載入是否已完成（change: notification-deeplink-chat-trigger）：
    // 供需要「確保聊天歷史已載入完成才動作」的呼叫端（例如深連結消費後 forceNewChat）避免
    // 在載入完成前就呼叫 createNewChat，讀到尚未載入的舊 chatHistory 閉包把剛載入的歷史蓋掉。
    isInitialized,
    createNewChat,
    updateChatTitle,
    updateChatMessages,
    updateMessageUserFeedback,
    switchToChat,
    deleteChat,
    getCurrentChat,
    getCurrentMessages,
    loadFromBackend,
    setError,
    notifyWsMessageReceived,
    promoteTemporaryChat,
  };
};
